
import React from "react";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderNetwork () {
    return (
      <div>
        <main className="transition-opacity duration-300">
          <div className="py-10">
    
            {/* Breadcrumbs */}
            <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
          <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
          <FiChevronRight />
          <span>Agent Setup Guide</span>
          <FiChevronRight />
          <span className="text-[#1b140d] dark:text-white font-medium">Network</span>
        </nav>
    
            {/* Intro */}
            <div className="mb-10 scroll-mt-24" id="Introduction">
              <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
                Documentation
              </span>
              <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                  Netraa SNMP v2c Device Monitoring
                </span>
              </h1>
              <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
                This guide explains how to monitor routers, firewalls, and switches using Netraa Orchestrator via SNMP v2c.
              </p>
            </div>
    
            {/* Deployment Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {[
                { title: "Deployment Type", value: "On-Premises" },
                { title: "Protocol", value: "SNMP v2c" },
                { title: "Monitoring Tool", value: "Netraa Orchestrator" },
              ].map((item, idx) => (
                <div key={idx} className="bg-surface-light dark:bg-surface-dark p-4 rounded-xl border border-border-color dark:border-border-dark shadow-sm">
                  <p className="text-[10px] uppercase font-bold text-text-muted mb-1">{item.title}</p>
                  <p className={`text-sm font-bold ${idx === 0 ? "text-primary" : "text-text-main dark:text-white"}`}>{item.value}</p>
                </div>
              ))}
            </div>
    
            {/* Important Note */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 mb-12 rounded-r-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="material-symbols-outlined text-amber-600 dark:text-amber-400 text-sm">warning</span>
                <h4 className="text-sm font-bold text-amber-800 dark:text-amber-300">IMPORTANT NOTE</h4>
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                In many environments, ICMP (ping) is disabled. Ping failure does NOT mean the device is unreachable. Always validate using SNMP.
              </p>
            </div>
    
            {/* 1. Deployment Overview */}
            <section className="mb-6 scroll-mt-24" id="overview">
              <h2 className="text-2xl font-bold mb-6">Deployment Overview</h2>
              <ul className="list-disc pl-5 space-y-2 text-text-muted dark:text-gray-400 text-sm">
                <li>Client provides an on-premises server</li>
                <li>Netraa Orchestrator Server is installed on this server</li>
                <li>All monitoring traffic originates only from this server</li>
                <li>Devices must allow SNMP v2c access from this server IP</li>
              </ul>
            </section>
    
            {/* 2. Network Reachability */}
            <section className="mb-6 scroll-mt-24" id="rechability">
              <h2 className="text-2xl font-bold mb-6">Network Reachability</h2>
    
              <div className="prose dark:prose-invert max-w-none text-sm space-y-3">
                <p>The Netraa Orchestrator server must be able to reach the management IP of the device.</p>
    
                <p><strong>Do NOT rely only on:</strong></p>
                <CodeBlock code={`ping <device-ip>`} />
    
                <p><strong>Recommended Connectivity Check:</strong></p>
                <CodeBlock code={`snmpwalk -v2c -c <community-string> <device-ip>`} />
    
                <ul className="list-disc pl-5 space-y-1 text-text-muted dark:text-gray-400">
                  <li>If SNMP responds, connectivity is working even if ping fails</li>
                </ul>
              </div>
            </section>
    
            {/* 3. Firewall / Whitelisting */}
            <section className="mb-6 scroll-mt-24" id="firewall">
              <h2 className="text-2xl font-bold mb-6">Firewall / Network Whitelisting</h2>
    
              <ul className="list-disc pl-5 space-y-2 text-sm text-text-muted dark:text-gray-400">
                <li>Protocol: UDP</li>
                <li>Port: 161 (SNMP)</li>
                <li>Source IP: Netraa Orchestrator Server IP only</li>
              </ul>
            </section>
    
            {/* 4. SNMP Configuration */}
            <section className="mb-6 scroll-mt-24" id="snmp">
              <h2 className="text-2xl font-bold mb-6">SNMP v2c Configuration</h2>
    
              <ul className="list-disc pl-5 space-y-2 text-sm text-text-muted dark:text-gray-400">
                <li>Enable SNMP v2c on device</li>
                <li>Configure read-only community string (example: netraa_ro)</li>
                <li>Restrict SNMP access to Netraa server IP only</li>
              </ul>
            </section>
    
            {/* 5. SNMP Validation */}
            <section className="mb-6 scroll-mt-24" id="validation">
              <h2 className="text-2xl font-bold mb-6">SNMP Validation</h2>
    
              <CodeBlock code={`snmpwalk -v2c -c <community-string> <device-ip>`} />
    
              <div className="mt-4 text-sm space-y-2 text-text-muted dark:text-gray-400">
                <p><strong>Expected Result:</strong></p>
                <ul className="list-disc pl-5">
                  <li>OID values should be returned</li>
                  <li>If response is received → SNMP is correctly configured</li>
                </ul>
    
                <p><strong>Troubleshooting:</strong></p>
                <ul className="list-disc pl-5">
                  <li>Check SNMP service enabled</li>
                  <li>Verify community string</li>
                  <li>Verify firewall rules (UDP 161)</li>
                  <li>Confirm source IP restriction</li>
                </ul>
              </div>
            </section>
    
            {/* 6. Netraa Configuration */}
            <section className="mb-6 scroll-mt-24" id="configuration">
              <h2 className="text-2xl font-bold mb-6">Netraa Orchestrator Configuration</h2>
    
              <ul className="list-disc pl-5 space-y-2 text-sm text-text-muted dark:text-gray-400">
                <li>Create Network Device Host</li>
                <li>Add SNMP Interface</li>
                <li>Provide Device IP, SNMP v2c, Community string</li>
                <li>Attach monitoring template (Router / Firewall / Switch)</li>
              </ul>
            </section>
    
            {/* Key Takeaways */}
            <section className="mb-6">
              <h2 className="text-2xl font-bold mb-6">Key Takeaways</h2>
    
              <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-900/30 p-6 rounded-xl">
                <ul className="list-disc pl-5 space-y-2 text-sm text-green-800 dark:text-green-300">
                  <li>Switches often block ping</li>
                  <li>Do not rely on ICMP</li>
                  <li>SNMP walk is the correct connectivity test</li>
                  <li>Monitoring works if UDP 161 + SNMP are configured</li>
                </ul>
              </div>
            </section>
    
          </div>
        </main>
      </div>
    );
    }